import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { ColumnFilterSpec, FilterValue } from "./columnFilters";
import styles from "./ColumnFilters.module.css";

/** The most values a set filter lists before it offers a search box. */
const searchFrom = 8;

/** A column's heading with its filter button, and the filter's popover when open. */
export function FilterHeading<T>({
  label,
  spec,
  rows,
  counted,
  filter,
  onChange,
}: {
  label: string;
  spec: ColumnFilterSpec<T>;
  rows: readonly T[];
  counted: readonly T[];
  filter: FilterValue | undefined;
  onChange: (filter: FilterValue | undefined) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();
  const active = filter !== undefined;
  const activeCount = filter?.kind === "set" ? filter.selected.size : 0;

  // The library makes a sortable heading a button, and a button may not hold another, so the filter button is drawn into the
  // header cell beside the heading rather than inside it. Outside a table there is no cell, and it stays beside the label.
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [cell, setCell] = useState<Element | null>(null);
  useLayoutEffect(() => {
    setCell(anchorRef.current?.closest('[role="columnheader"]') ?? null);
  }, []);

  const control = (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={cell ? `${styles.button} ${styles.inCell}` : styles.button}
        data-active={active || undefined}
        aria-label={active ? t("dataTable.filter.buttonActive", { column: label }) : t("dataTable.filter.button", { column: label })}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Escape" && open) {
            setOpen(false);
          }
        }}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M2 3h12l-4.5 5.5V13l-3-1.5V8.5z" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" />
        </svg>
        {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
      </button>
      {open && (
        <FilterPopover id={popoverId} anchor={buttonRef} label={label} onClose={() => setOpen(false)}>
          {spec.kind === "text" ? (
            <TextFilter label={label} filter={filter?.kind === "text" ? filter.text : ""} onChange={(text) => onChange(text.trim() === "" ? undefined : { kind: "text", text })} />
          ) : (
            <SetFilter spec={spec} rows={rows} counted={counted} selected={filter?.kind === "set" ? filter.selected : undefined} onChange={(selected) => onChange(selected === undefined ? undefined : { kind: "set", selected })} />
          )}
        </FilterPopover>
      )}
    </>
  );

  return (
    <>
      <span ref={anchorRef} className={cell ? `${styles.heading} ${styles.headingInCell}` : styles.heading}>
        <span className={styles.label}>{label}</span>
        {!cell && control}
      </span>
      {cell && createPortal(control, cell)}
    </>
  );
}

/**
 * The filter's popover, drawn over the page beneath its button so the table's rounded card cannot clip it. It closes on Escape
 * (returning focus to the button), on a click outside it, and when the page scrolls or resizes. Events inside it do not reach the
 * column heading, which would otherwise sort.
 */
function FilterPopover({ id, anchor, label, onClose, children }: { id: string; anchor: React.RefObject<HTMLButtonElement | null>; label: string; onClose: () => void; children: ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const button = anchor.current;
    const popover = ref.current;
    if (!button || !popover) {
      return;
    }
    const bounds = button.getBoundingClientRect();
    const width = popover.offsetWidth;
    // Below the button, its left edge at the button's, kept inside the window.
    setPosition({ top: bounds.bottom + 6, left: Math.max(8, Math.min(bounds.left - 8, window.innerWidth - width - 8)) });
  }, [anchor]);

  // Focus moves into the popover as it opens (to its box, where it has one), so the keyboard is there and Escape closes it.
  useEffect(() => {
    if (!ref.current?.contains(document.activeElement)) {
      ref.current?.focus();
    }
  }, []);

  useEffect(() => {
    const close = (): void => onClose();
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (!ref.current?.contains(target) && !anchor.current?.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("resize", close);
    document.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("resize", close);
      document.removeEventListener("scroll", close, true);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      id={id}
      role="dialog"
      tabIndex={-1}
      aria-label={t("dataTable.filter.dialog", { column: label })}
      className={styles.popover}
      style={position ? { top: position.top, left: position.left } : { visibility: "hidden" }}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") {
          onClose();
          anchor.current?.focus();
        }
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** A text column's filter: one box, keeping the rows that contain what is typed, as it is typed. */
function TextFilter({ label, filter, onChange }: { label: string; filter: string; onChange: (text: string) => void }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={styles.text}>
      <input
        type="search"
        className={styles.input}
        value={filter}
        placeholder={t("dataTable.filter.contains")}
        aria-label={t("dataTable.filter.containsLabel", { column: label })}
        autoFocus
        onChange={(event) => onChange(event.target.value)}
      />
      {filter !== "" && (
        <button type="button" className={styles.link} onClick={() => onChange("")}>
          {t("dataTable.filter.clear")}
        </button>
      )}
    </div>
  );
}

/**
 * A set column's filter: its values as a checklist, each with how many rows have it, applied as they are ticked. "Only" beside a
 * value keeps that value alone; "All" and "None" tick or clear the lot. A long list can be searched. Every value ticked is the same as
 * no filter.
 */
function SetFilter<T>({
  spec,
  rows,
  counted,
  selected,
  onChange,
}: {
  spec: ColumnFilterSpec<T>;
  rows: readonly T[];
  counted: readonly T[];
  selected: ReadonlySet<string> | undefined;
  onChange: (selected: ReadonlySet<string> | undefined) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const values = useMemo(() => [...new Set(rows.map(spec.value))].sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b))), [rows, spec]);
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of counted) {
      const value = spec.value(row);
      map.set(value, (map.get(value) ?? 0) + 1);
    }
    return map;
  }, [counted, spec]);
  const shown = values.filter((value) => (value === "" ? t("dataTable.filter.blank") : value).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const isTicked = (value: string): boolean => selected === undefined || selected.has(value);

  const commit = (next: Set<string>): void => onChange(next.size === values.length ? undefined : next);
  const toggle = (value: string): void => {
    const next = new Set(selected ?? values);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    commit(next);
  };

  return (
    <div className={styles.set}>
      {values.length > searchFrom && (
        <input type="search" className={styles.input} value={search} placeholder={t("dataTable.filter.search")} aria-label={t("dataTable.filter.search")} autoFocus onChange={(event) => setSearch(event.target.value)} />
      )}
      <div className={styles.actions}>
        <button type="button" className={styles.link} onClick={() => onChange(undefined)}>
          {t("dataTable.filter.all")}
        </button>
        <button type="button" className={styles.link} onClick={() => onChange(new Set())}>
          {t("dataTable.filter.none")}
        </button>
      </div>
      {shown.length === 0 ? (
        <p className={styles.empty}>{t("dataTable.filter.noMatches")}</p>
      ) : (
        <ul className={styles.list}>
          {shown.map((value) => {
            const name = value === "" ? t("dataTable.filter.blank") : value;
            return (
              <li key={value} className={styles.option}>
                <label className={styles.check} data-ticked={isTicked(value) || undefined}>
                  <input type="checkbox" checked={isTicked(value)} onChange={() => toggle(value)} />
                  <span className={styles.tick} aria-hidden="true" />
                  <span className={value === "" ? `${styles.name} ${styles.blank}` : styles.name}>{name}</span>
                  <span className={styles.count}>{counts.get(value) ?? 0}</span>
                </label>
                <button type="button" className={styles.only} aria-label={t("dataTable.filter.onlyLabel", { value: name })} onClick={() => commit(new Set([value]))}>
                  {t("dataTable.filter.only")}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
